/**
 * The difference between the permissions a skill already has and the ones the incoming version
 * asks for.
 *
 * The point of the diff is the gain. A person approving an install is deciding whether to hand
 * over authority the project does not have yet, and a list of the new version's permissions does
 * not answer that: most of them are already granted, and the two that are not are buried.
 *
 * Nothing here decides whether approval is needed. A change that only removes permission is
 * still shown, because an approver may care about a capability disappearing, but the caller can
 * tell the two apart from `added` being empty.
 */
export interface PermissionDiff {
  /** Permissions the incoming version adds. This is what an approver is deciding. */
  added: string[];
  /** Permissions the current version has and the incoming one does not. */
  removed: string[];
  /** Permissions both versions carry, so a reader can see the diff is against something. */
  unchanged: string[];
}

/** Compares two permission lists, ignoring order and duplicates. */
export function permissionDiff(
  current: readonly string[],
  incoming: readonly string[],
): PermissionDiff {
  const before = new Set(current.map((entry) => entry.trim()).filter((entry) => entry !== ""));
  const after = new Set(incoming.map((entry) => entry.trim()).filter((entry) => entry !== ""));
  const added = [...after].filter((entry) => !before.has(entry)).sort();
  const removed = [...before].filter((entry) => !after.has(entry)).sort();
  const unchanged = [...after].filter((entry) => before.has(entry)).sort();
  return { added, removed, unchanged };
}

/**
 * One sentence an approver can act on, or null when the incoming version asks for no more than
 * the project already has.
 */
export function diffSummary(diff: PermissionDiff): string | null {
  if (diff.added.length === 0) {
    return null;
  }
  if (diff.added.length === 1) {
    return `This adds 1 permission: ${diff.added[0]}.`;
  }
  return `This adds ${diff.added.length} permissions.`;
}
